import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { loginWithOAuth, loginWithOAuthFacebook } from '../../service/api';
import { AuthContext } from '../../context/auth';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import LoadingEllipsis from '../../components/LoadingEllipsis/LoadingEllipsis';
import './styles/Login.css';
import './styles/FooterLogin.css';
import '../../Mobile.css';

import welcomeLogo from '../../assets/img/fruitLogo.png';
import bckgVideo from '../../assets/video/lemons.mp4';
import fallbackVideo from '../../assets/video/oranges.mp4';
import fallbackImg from '../../assets/img/fallback.webp';
import fallbackMobileImg from '../../assets/img/ban1.webp';

const Login = () => {
  const { login } = useContext(AuthContext);
  const { handleOAuthLogin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [videoError, setVideoError] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());
  const videoRef = useRef();

  const handleVideoError = () => setVideoError(true);

  const validationSchema = Yup.object({
    email: Yup.string().email('Email inválido').required('Obrigatório'),
    password: Yup.string().min(6, 'Mínimo 6 caracteres').required('Obrigatório'),
    cbForgot: Yup.boolean(),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setMessage('');

    if (values.cbForgot) {
      navigate('/forgot');
      return;
    }

    try {
      const isAuthenticated = await login(values.email, values.password);
      if (isAuthenticated) {
        setMessage('Bem-vindo!');
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        setMessage(error.response.data);
      } else if (error?.response?.status === 401) {
        setMessage('Credenciais incorretas');
      } else {
        setMessage('Falha ao conectar');
      }
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (window.google) {
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'popup',
        redirect_uri: window.location.origin,
        callback: async (response) => {
          try {
            const res = await loginWithOAuth({
              code: response.code,
              redirect_uri: window.location.origin,
            });

            const { token, user } = res;
            handleOAuthLogin(user, token);
            navigate('/home/consumer/onboarding_consumer');
          } catch (error) {
            console.error('Erro no login com Google:', error);
            setMessage(error?.response?.data?.message || 'Erro na autenticação');
          }
        },
      });

      // Salva o client para uso posterior
      window._googleClient = client;
    }
  }, []);

  const handleFacebookLogin = () => {
    setLoading(true);
    setMessage('');

    // Aguarda o carregamento do SDK
    if (!window.FB || typeof window.FB.login !== 'function') {
      setMessage('Serviço do Facebook ainda não carregado. Tente novamente em alguns segundos.');
      setLoading(false);
      return;
    }


    window.FB.login((response) => {
      if (response.authResponse) {
        const { accessToken } = response.authResponse;

        // Envolva a chamada assíncrona dentro de uma IIFE
        (async () => {
          try {
            const res = await loginWithOAuthFacebook({
              code: accessToken,
              redirect_uri: window.location.origin,
            });

            const { token, user } = res;
            handleOAuthLogin(user, token);
            navigate('/home/consumer/onboarding_consumer');
          } catch (error) {
            console.error('Erro no login com Facebook:', error);
            setMessage(error?.response?.data?.message || 'Erro na autenticação com Facebook');
          } finally {
            setLoading(false);
          }
        })();
      } else {
        setMessage('Login com Facebook cancelado');
        setLoading(false);
      }
    }, { scope: 'email,public_profile' });
  };



  return (
    <>
      <div className="loginVideoBox">
        {isMobile ? (
          <video ref={videoRef} muted autoPlay loop onError={handleVideoError}>
            <source src={fallbackVideo} type="video/mp4" />
            <img src={fallbackMobileImg} className="loginFallbackImg" alt="Fallback Mobile Image" />
          </video>
        ) : !videoError ? (
          <video ref={videoRef} muted autoPlay loop onError={handleVideoError}>
            <source src={bckgVideo} type="video/mp4" onError={() => (videoRef.current.src = fallbackVideo)} />
            <source src={fallbackVideo} type="video/mp4" />
          </video>
        ) : (
          <img src={fallbackImg} className="loginFallbackImg" alt="Fallback Image" />
        )}
      </div>

      <div className="login-container">
        <div className="title">
          <img src={welcomeLogo} alt="icone da maçã" />
          <span>Bem-vindo ao Nutrição App</span>
        </div>

        <div className="loginBox">
          <span> Faça seu Login </span>
          {loading && <div className="message"><LoadingEllipsis message='Aguarde' /></div>}
          {message && <div className="message">{message}</div>}

          <Formik
            initialValues={{ email: '', password: '', cbForgot: false }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange }) => (
              <Form>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">Usuário</label>
                  <Field type="email" name="email" className="form-control" />
                  <ErrorMessage name="email" component="div" className="error-message" />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="password">Senha</label>
                  <Field type="password" name="password" className="form-control" />
                  <ErrorMessage name="password" component="div" className="error-message" />
                </div>

                <div className="loginBtn">
                  <button type="submit" className="form-btn btn">Enviar</button>
                </div>

                <div className="divLines"><hr className='line' /> ou <hr className='line' /></div>
                <div className="form-group">
                  <div onClick={() => navigate('/forgot')} className="linkForgot">Esqueci a senha</div>

                  <div onClick={() => navigate('/sign')} className="linkSignup">Registre-se aqui</div>
                </div>

                {/* 
                <div className="form-group">
                  <div className="divLines"><hr className='line' /> ou <hr className='line' /></div>
                  <div className="google-btn" onClick={() => {
                    if (window._googleClient) {
                      window._googleClient.requestCode();
                    } else {
                      setMessage('Serviço do Google não disponível.');
                    }
                  }}>
                    <img src="/fruitLogo.png" alt="Google" className="google-icon" />
                    <div className="btnGoogleText">
                      Continuar com Google
                    </div>
                  </div>
                  <br />
                  <div className="google-btn" onClick={handleFacebookLogin}>
                    <img src="/fruitLogo.png" alt="Facebook" className="google-icon" />
                    <div className="btnGoogleText">
                      Continuar com Facebook
                    </div>
                  </div>
                </div>
                 */}
              </Form>
            )}
          </Formik>
        </div>
      </div >

      <div className="footerLogin">
        <div className="year">2007 - <span>{currentYear}</span></div>
        <div>&copy; todos os direitos reservados</div>
        <div>powered by <span className="brand">apit solutions</span></div>
      </div>
    </>
  );
};

export default Login;
